import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AdvertType, Offer } from 'src/app/shared/constants/offer';
import { Page } from 'src/app/shared/models/Page';
import { APIService } from 'src/app/shared/services/api.service';
import { CacheService } from 'src/app/shared/services/cache.service';
import { PaginationService } from 'src/app/shared/services/pagination.service';
import { environment } from 'src/environments/environment';

import urlJoin from 'url-join';
import { CampaignMeta } from '../models/Auth';
import { Observable } from 'rxjs';
import { BulkOperation } from '../decorators/bulk-operation.decorator';

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private baseClientUrl = urlJoin(environment.API_URL, '/');

  creationStatus: any;
  defaultOrderingColumn = 'id';

  constructor(
    private http: HttpClient,
    private apiService: APIService,
    private paginationService: PaginationService,
    private cache: CacheService,
  ) { }

  public static createOfferMediaMappingFilter(offerMediaMappings: any, field: string | number) {
    // Extract the specified field from all objects and flatten the arrays
    const extractedData = offerMediaMappings.flatMap((obj: { media_info: Record<string, any>; }) => {
      const fieldData = obj.media_info[field];

      if (!fieldData) {
        return [{
          id: obj.media_info.id,
          name: obj.media_info.name,
        }];
      }

      if (Array.isArray(fieldData)) {
        return fieldData.map((item) => ({
          id: item.id,
          name: item.name,
        }));
      }

      // Handle the case when the field is not an array
      return [{
        id: fieldData.id,
        name: fieldData.name,
      }];

    });

    // Sort the data by name
    const sortedData = extractedData.sort((a: { name: string; }, b: { name: string; }) => a.name.localeCompare(b.name));

    // Remove duplicates based on ID
    const uniqueData = sortedData.filter((item: { id: number; }, index: number, self: { id: number; }[]) =>
      index === self.findIndex((i: { id: number; }) => i.id === item.id),
    );

    return uniqueData;
  }

  public getOfferList(params = {}, lean = false) {
    const url = this.apiService.url('offer');

    if (lean) {
      params = {
        ...params,
        lean: 'true',
      };
    }

    return this.http.get(url, {
      params,
    });
  }

  public getLeanOfferList(params = {}) {
    const url = this.apiService.url('offer_selection');

    return this.http.get(url, {
      params,
    });
  }

  public getOfferListPaginated(params = {}, page = 1, skipCache = false) {
    const url = this.apiService.url(
      'offer',
    );

    const queryParams = {
      ...params,
      ...this.paginationService.pagination(page, 25),
    };

    if (Object.prototype.hasOwnProperty.call(params, 'ordering')) {
      queryParams['ordering'] = params['ordering'];
    } else {
      /**
       * This ensures that no duplicate offers are shown when the user
       * switches between pages.
       */
      queryParams['ordering'] = this.defaultOrderingColumn;
    }

    return this.http.get<Page>(url, {
      headers: {
        ...this.cache.buildCacheSkipHeader(skipCache),
      },
      params: queryParams,
    });
  }

  public getOffer(id: number, skipCache = false) {
    const url = this.apiService.url(`offer/${id}`);
    return this.http.get<Offer>(url, {
      headers: {
        ...this.cache.buildCacheSkipHeader(skipCache),
      },
    }).toPromise();
  }

  public createOffer(body) {
    const url = this.apiService.url('offer');
    return this.http.post(url, body).toPromise();
  }

  public updateOffer(id: number, body) {
    const url = this.apiService.url(`offer/${id}/`);
    return this.http.patch(url, body).toPromise();
  }

  public getAdSpaceMediaMapping(params = {}, skipCache = false) {
    const url = this.apiService.url('offer_media_mapping');
    return this.http.get(url, {
      params,
      headers: this.cache.buildCacheSkipHeader(skipCache),
    });
  }

  public getSingleAdSpaceMediaMapping(id) {
    const url = this.apiService.url(`offer_media_mapping/${id}/`);
    return this.http.get(url);
  }

  public updateSingleAdSpaceMediaMapping(id, body) {
    const url = this.apiService.url(`offer_media_mapping/${id}/`);
    return this.http.patch(url, body);
  }

  /**
   * Updates the status of multiple offerMediaMappings at once.
   * @param updates Maps offerMediaMapping ids to the status they should have
   */
  @BulkOperation()
  public bulkPatchAdSpaceMediaMapping(updates, fieldName: string) {
    const url = this.apiService.url('offer_media_mapping_bulk/');
    const body = {
      updates: { [fieldName]: updates },
    };

    return this.http.patch(url, body);
  }

  @BulkOperation()
  public bulkAddAdSpacesToCampaign(offer, adSpaces) {
    const url = this.apiService.url('offer_media_mapping_bulk/');
    return this.http.post(url, { offer, media_list: adSpaces });
  }

  public getCommissionModel(params = {}) {
    const url = this.apiService.url('commission_model');
    return this.http.get(url, {
      params,
      headers: this.cache.buildCacheSkipHeader(true),
    });
  }

  public getDeviceGroups() {
    const url = this.apiService.url('device_group');
    return this.http.get(url);
  }

  public applyForOffer(body) {
    const url = this.apiService.url('offer_media_mapping');
    return this.http.post(url, body);
  }

  public updateOfferAdSpaceMapping(mappingID, params) {
    const url = this.apiService.url('offer_media_mapping/{id}/', {
      id: mappingID,
    });

    return this.http.patch(url, params);
  }

  public getDefaultAdvertisement(id: number)  {
    const url = urlJoin(this.baseClientUrl, `offer/${id}/default_ad`);
    return this.http.get(url);
  }

  public getAdvertisements(advertType: AdvertType, params = {}) {
    let linkPart;
    switch (advertType) {
      case AdvertType.TEXT_LINK:
        linkPart = 'links';
        break;
      case AdvertType.BANNER:
        linkPart = 'banners';
        break;
      case AdvertType.PRODUCT_FEED:
        linkPart = 'feeds';
        break;
      case AdvertType.RICH_MEDIA:
        linkPart = 'rich_media';
        break;
      case AdvertType.VOUCHER:
        linkPart = 'deals';
        break;
    }
    const url = urlJoin(this.baseClientUrl, `/advertisement_${linkPart}/`);
    return this.http.get(url, { params });
  }

  public getAdvertisementCode(params = {}) {
    const url = urlJoin(this.baseClientUrl, '/advertisement_code/');
    return this.http.get(url, { params });
  }

  public getAdvertisementMediaMapping(params = {}) {
    const url = urlJoin(
      this.baseClientUrl,
      '/advert_media_mapping/',
    );

    return this.http.get(url, {
      params,
      headers: this.cache.buildCacheSkipHeader(true),
    });
  }

  public updateAdvertCode(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_code/${id}/`);
    return this.http.patch(url, body);
  }

  public getAdvertisementLinks(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_links/?offer=${id}`);
    return this.http.get(url);
  }

  public getAdvertisementBanners(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_banners/?offer=${id}`);
    return this.http.get(url);
  }

  public getAdvertisementRichMedias(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_rich_media/?offer=${id}`);
    return this.http.get(url);
  }

  public getAdvertisementDeals(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_deals/?offer=${id}`);
    return this.http.get(url);
  }

  public createAdvertisementLinks(body) {
    const url = urlJoin(this.baseClientUrl, '/advertisement_links/');
    return this.http.post(url, body);
  }

  public createAdvertisementBanners(body) {
    const url = urlJoin(this.baseClientUrl, '/advertisement_banners/');
    return this.http.post(url, body);
  }

  public createAdvertisementRichMedia(body) {
    const url = urlJoin(this.baseClientUrl, '/advertisement_rich_media/');
    return this.http.post(url, body);
  }

  public createAdvertisementDeals(body) {
    const url = urlJoin(this.baseClientUrl, '/advertisement_deals/');
    return this.http.post(url, body);
  }

  public getAllAdvertisements(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisements/?offer=${id}`);
    return this.http.get(url);
  }

  public getTextLink(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_links/${id}/`);
    return this.http.get(url);
  }

  public getBanner(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_banners/${id}/`);
    return this.http.get(url);
  }

  public getRichMedia(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_rich_media/${id}/`);
    return this.http.get(url);
  }

  public getDeal(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_deals/${id}/`);
    return this.http.get(url);
  }

  public updateTextLink(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_links/${id}/`);
    return this.http.patch(url, body);
  }

  public updateBanner(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_banners/${id}/`);
    return this.http.patch(url, body);
  }

  public updateRichMedia(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_rich_media/${id}/`);
    return this.http.patch(url, body);
  }

  public updateDeals(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/advertisement_deals/${id}/`);
    return this.http.patch(url, body);
  }

  public getAdvertisementProductFeed(id: number) {
    const url = urlJoin(
      this.baseClientUrl,
      '/advertisement_feeds/',
    );

    const params = {
      offer: id as any,
    };

    return this.http.get(url, {
      params,
    });
  }

  public createProductFeed(payload) {
    const url = urlJoin(this.baseClientUrl, '/advertisement_feeds/');
    return this.http.post(url, payload);
  }

  public updateProductFeed(id: number, payload: any) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${id}/`,
    );

    return this.http.patch(
      url,
      payload,
    );
  }

  public deleteProductFeed(id: number) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${id}/`,
    );

    return this.http.delete(
      url,
    );
  }

  public requestProductFeedSync(id: number) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${id}/sync/`,
    );

    return this.http.get(url, {
      headers: this.cache.buildCacheSkipHeader(true),
    });
  }

  public getProductFeedActualColumns(id: number) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${id}/actual_columns/`,
    );

    return this.http.get(url, {
      headers: this.cache.buildCacheSkipHeader(true),
    });
  }

  public getAllProductFeedEntries(id: number) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${id}/entries/`,
    );

    return this.http.get(
      url,
    );
  }

  public getProductFeedEntry(feed: number, entry: number) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${feed}/entry/${entry}/`,
    );

    return this.http.get(
      url,
    );
  }

  public getProductFeedPreviewEntry(feed: number) {
    const url = urlJoin(
      this.baseClientUrl,
      `/advertisement_feeds/${feed}/preview/`,
    );

    return this.http.get(
      url,
    );
  }

  public deleteAdvertisement(id: number) {
    const url = urlJoin(this.baseClientUrl, `/advertisements/${id}`);
    return this.http.delete(url);
  }

  public getAllPriceSegements(params = {}) {
    const url = urlJoin(this.baseClientUrl, '/price_segment/');
    return this.http.get(url, { params });
  }

  public getPriceSegements(id: number) {
    const url = urlJoin(this.baseClientUrl, `/price_segment/?offer=${id}`);
    return this.http.get(url);
  }

  public getPriceSegement(id: number) {
    const url = urlJoin(this.baseClientUrl, `/price_segment/${id}/`);
    return this.http.get(url);
  }

  public createPriceSegment(body) {
    const url = urlJoin(this.baseClientUrl, '/price_segment/');
    return this.http.post(url, body);
  }

  public updatePriceSegment(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/price_segment/${id}/`);
    return this.http.patch(url, body);
  }

  public deletePriceSegment(id: number) {
    const url = urlJoin(this.baseClientUrl, `/price_segment/${id}`);
    return this.http.delete(url);
  }

  public getCommissionModels(id: number) {
    const url = urlJoin(this.baseClientUrl, `/commission_model/${id}/`);
    return this.http.get(url);
  }

  public getAllCommissionModels() {
    const url = urlJoin(this.baseClientUrl, '/commission_model/');
    return this.http.get(url);
  }

  public createCommissionModels(body) {
    const url = urlJoin(this.baseClientUrl, '/commission_model/');
    return this.http.post(url, body);
  }

  public updateCommissionModels(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/commission_model/${id}/`);
    return this.http.patch(url, body);
  }

  public deletePriceCombination(id: number) {
    const url = urlJoin(this.baseClientUrl, `/price_combination/${id}/`);
    return this.http.delete(url);
  }

  public createPriceCombination(body) {
    const url = urlJoin(this.baseClientUrl, '/price_combination/');
    return this.http.post(url, body);
  }

  public updatePriceCombination(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/price_combination/${id}/`);
    return this.http.patch(url, body);
  }

  public getPriceCombination(id: number) {
    const url = urlJoin(this.baseClientUrl, `/price_combination/${id}/`);
    return this.http.get(url);
  }

  public createEventId(body) {
    const url = urlJoin(this.baseClientUrl, '/event_id/');
    return this.http.post(url, body).toPromise();
  }

  public getEventId(id: number) {
    const url = urlJoin(this.baseClientUrl, `/event_id/${id}/`);
    return this.http.get(url).toPromise();
  }

  public updateEventId(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `/event_id/${id}/`);
    return this.http.patch(url, body).toPromise();
  }

  public deleteEventId(id: number) {
    const url = urlJoin(this.baseClientUrl, `/event_id/${id}/`);
    return this.http.delete(url).toPromise();
  }

  public getEventIds(params = {}) {
    const url = urlJoin(this.baseClientUrl, '/event_id/');
    return this.http.get(url, { params }).toPromise();
  }

  public getOfferFromCampaignSlug(campaignSlug: string): Observable<CampaignMeta> {
    const url = urlJoin(this.baseClientUrl, `/api/v1/offers/${campaignSlug}/via_slug/`);
    return this.http.get(url)
  }
}
